/**
 * OAuth Login Page for ChatGPT Integration
 * 
 * This page is displayed when ChatGPT redirects users to authorize access.
 * It uses the existing Supabase authentication and generates OAuth codes.
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';

export default function OAuthLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // OAuth parameters from URL
  const searchParams = new URLSearchParams(window.location.search);
  const state = searchParams.get('state');
  const redirectUri = searchParams.get('redirect_uri');
  const clientId = searchParams.get('client_id');

  useEffect(() => {
    // Validate OAuth parameters
    if (!state || !redirectUri) {
      setError('Parâmetros OAuth inválidos. Por favor, tente novamente do ChatGPT.');
    }
  }, [state, redirectUri]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!state || !redirectUri) {
      setError('Parâmetros OAuth inválidos.');
      return;
    }

    console.log('[OAUTH-LOGIN] Starting login with state:', state);
    console.log('[OAUTH-LOGIN] Redirect URI:', redirectUri);

    setLoading(true);
    setError(null);

    try {
      // Call the OAuth token endpoint (worker OAuth proxy)
      const response = await fetch('https://oauth-proxy.tigraodegente.workers.dev/oauth/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          grant_type: 'password',
          email,
          password,
          state,
          redirect_uri: redirectUri,
        }),
      });
      
      console.log('[OAUTH-LOGIN] Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Falha na autenticação');
      }

      const data = await response.json();
      const authCode = data.code;
      const returnedState = data.state;

      console.log('[OAUTH-LOGIN] Received code:', authCode);
      console.log('[OAUTH-LOGIN] Sent state:', state);
      console.log('[OAUTH-LOGIN] Received state:', returnedState);

      if (!authCode) {
        throw new Error('Código de autorização não foi gerado');
      }

      setSuccess(true);

      // Redirect back to ChatGPT with the code
      const redirectUrl = new URL(redirectUri);
      redirectUrl.searchParams.set('code', authCode);
      redirectUrl.searchParams.set('state', state); // Use the ORIGINAL state from URL params

      console.log('[OAUTH-LOGIN] Redirecting to:', redirectUrl.toString());

      // Delay to show success message
      setTimeout(() => {
        window.location.href = redirectUrl.toString();
      }, 1500);

    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Erro ao fazer login. Verifique suas credenciais.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/20 via-background to-primary/10 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle2 className="h-16 w-16 text-green-500" />
            </div>
            <CardTitle className="text-2xl">Autorização Bem-Sucedida!</CardTitle>
            <CardDescription>
              Redirecionando você de volta ao ChatGPT...
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/20 via-background to-primary/10 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <svg
              className="h-12 w-12 text-primary"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <CardTitle className="text-2xl">Autorizar Cash Flow Pro</CardTitle>
          <CardDescription>
            O ChatGPT está solicitando acesso à sua conta para ajudá-lo a gerenciar suas finanças.
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleLogin}>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="bg-muted p-3 rounded-lg text-sm">
              <p className="font-medium mb-1">Permissões solicitadas:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Ler suas transações e saldos</li>
                <li>Criar e editar transações</li>
                <li>Visualizar relatórios financeiros</li>
              </ul>
            </div>
          </CardContent>

          <CardFooter className="flex flex-col space-y-2">
            <Button
              type="submit"
              className="w-full"
              disabled={loading || !state || !redirectUri}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Autorizando...
                </>
              ) : (
                'Autorizar Acesso'
              )}
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => {
                if (redirectUri) {
                  const redirectUrl = new URL(redirectUri);
                  redirectUrl.searchParams.set('error', 'access_denied');
                  redirectUrl.searchParams.set('state', state || '');
                  window.location.href = redirectUrl.toString();
                }
              }}
              disabled={loading}
            >
              Cancelar
            </Button>

            <p className="text-xs text-center text-muted-foreground mt-4">
              Ao autorizar, você concorda que o ChatGPT tenha acesso aos dados mencionados acima.
              Você pode revogar o acesso a qualquer momento nas configurações da sua conta.
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}

